import { useState } from "react";
import ScrollReveal from "@/components/ScrollReveal";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { ArrowRight, Loader2, MapPin, Phone, Clock, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your inquiry has been received. We will be in touch.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-28 md:py-36 lg:py-44 px-6 md:px-12 border-t border-slate-800"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0A1325",
            border: "1px solid #1E293B",
            color: "#F8FAFC",
          },
        }}
      />
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {/* Left: Institutional Info */}
          <div className="lg:col-span-4">
            <ScrollReveal>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-4">
                Get in Touch
              </p>
              <h2
                data-testid="contact-heading"
                className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight mb-6"
              >
                Schedule a
                <br />
                Consultation
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-12">
                For institutional inquiries, partnership opportunities, or to
                learn more about our sovereign infrastructure solutions.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <MapPin className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs tracking-[0.12em] uppercase text-slate-500 mb-1">Office</p>
                    <p className="text-sm text-slate-300">
                      J. Carter Asset Management, LLC
                      <br />
                      Nashville, Tennessee
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs tracking-[0.12em] uppercase text-slate-500 mb-1">Institutional Desk</p>
                    <p className="text-sm text-slate-300">+1 (615) 555-0142</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs tracking-[0.12em] uppercase text-slate-500 mb-1">Hours</p>
                    <p className="text-sm text-slate-300">Mon&ndash;Fri, 8:00 AM &ndash; 6:00 PM CT</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="border-t border-slate-800 pt-6 mb-8">
                <p className="text-[10px] tracking-[0.2em] uppercase text-slate-600 mb-3">
                  Response Time
                </p>
                <p className="text-sm text-slate-400">
                  Institutional inquiries receive a response within 24 business hours from a senior relationship manager.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <a
                data-testid="contact-download-pdf"
                href={`${BACKEND_URL}/api/documents/institutional-overview`}
                className="inline-flex items-center gap-2.5 text-xs text-slate-400 hover:text-[#D4AF37] tracking-[0.1em] transition-colors duration-300 border border-slate-800 hover:border-[#D4AF37]/40 px-5 py-3"
              >
                <FileText className="w-3.5 h-3.5" />
                Download Institutional Overview
              </a>
            </ScrollReveal>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-8">
            <ScrollReveal delay={0.15}>
              <div className="border border-slate-800 bg-[#0A1325]/30 p-8 md:p-12">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                        Full Name *
                      </label>
                      <input
                        data-testid="contact-name-input"
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                        placeholder="John Carter"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                        Email Address *
                      </label>
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                        placeholder="j.carter@institution.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                      Organization
                    </label>
                    <input
                      data-testid="contact-org-input"
                      type="text"
                      name="organization"
                      value={form.organization}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                      placeholder="Your Institution"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                      Message *
                    </label>
                    <textarea
                      data-testid="contact-message-input"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 resize-none placeholder:text-slate-600"
                      placeholder="How can we assist your institution?"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    <p className="text-[10px] text-slate-600 tracking-wide hidden md:block">
                      All communications are encrypted and confidential.
                    </p>
                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={loading}
                      className="flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] px-10 py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Submit Inquiry
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
